import { Alert, AlertTitle } from '@mui/material';
import { tr, uiLocale } from '../lib/i18n';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { MeResponse } from '../api/types';
import { useAuth } from '../auth/AuthContext';

const day = (d: string | null) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(uiLocale(), { dateStyle: 'long' }) : '');
const days = (n: number) => tr(n === 1 ? 'يوم واحد' : n === 2 ? 'يومان' : n <= 10 ? `${n} أيام` : `${n} يوماً`, `${n} day${n === 1 ? '' : 's'}`);

/**
 * Subscription banner: before the end date (owner / settings managers only), during the grace
 * period and in read-only mode (everyone — it affects their work). Re-checked every few minutes,
 * so a renewal clears it without logging out.
 */
export function SubscriptionBanner() {
  const { me, can } = useAuth();
  const q = useQuery({
    queryKey: ['me', 'subscription'],
    queryFn: () => api<MeResponse>('/me'),
    initialData: me ?? undefined,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
    enabled: !!me,
  });
  const s = q.data?.subscription;
  if (!s || s.access === 'NONE' || s.access === 'ACTIVE') return null;

  if (s.access === 'EXPIRING') {
    if (!can('org.settings')) return null;
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {s.daysLeft === 0 ? tr('ينتهي اشتراككم اليوم', 'Your subscription expires today') : tr(`ينتهي اشتراككم خلال ${days(s.daysLeft ?? 0)}`, `Your subscription expires in ${days(s.daysLeft ?? 0)}`)} ({day(s.endsAt)}){s.planName ? tr(` — باقة ${s.planName}`, ` — ${s.planNameEn || s.planName} plan`) : ''}. {tr('تواصلوا معنا للتجديد حتى لا يتوقف العمل.', 'Contact us to renew and avoid interruption.')}
      </Alert>
    );
  }
  if (s.access === 'GRACE') {
    const left = Math.max(0, Math.round((Date.parse(`${s.graceEndsAt}T00:00:00Z`) - Date.parse(`${new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(new Date())}T00:00:00Z`)) / 86_400_000));
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <AlertTitle>{tr(`انتهى اشتراك الشركة في ${day(s.endsAt)}`, `The organization subscription expired on ${day(s.endsAt)}`)}</AlertTitle>
        {tr(`يعمل النظام بشكل طبيعي لمدة ${left === 0 ? 'آخر يوم' : days(left)} إضافية، بعدها يصبح للعرض فقط ويتوقف رابط النموذج العام. تواصلوا معنا للتجديد.`, `The system remains fully available for ${left === 0 ? 'one final day' : days(left)}. It will then become view-only and the public form will stop. Contact us to renew.`)}
      </Alert>
    );
  }
  return (
    <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
      <AlertTitle>{tr('الاشتراك منتهٍ — النظام للعرض فقط', 'Subscription expired — view-only mode')}</AlertTitle>
      {tr('يمكنكم تصفح بياناتكم، لكن لا يمكن إضافة أو تعديل أي شيء، ورابط النموذج العام متوقف. تواصلوا معنا لتجديد الاشتراك وسيعود كل شيء فوراً.', 'You can browse your data, but cannot add or edit anything, and the public form is disabled. Contact us to renew access.')}
    </Alert>
  );
}
